import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/manufacturing", label: "Process" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 z-50 w-full transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-white/85 border-b border-black/5 py-3" : "bg-transparent py-5"
      }`}
      data-testid="site-navbar"
    >
      <nav className="mx-auto flex max-w-[1600px] items-center justify-between px-6 md:px-12 lg:px-16">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo" onClick={() => window.scrollTo(0, 0)}>
          <img src="/bwt-logo.png" alt="BWT logo" className="h-11 w-11 rounded-full bg-white object-contain p-0.5 md:h-12 md:w-12" />
          <span className="flex flex-col leading-none">
            <span className={`font-display text-sm font-extrabold tracking-tight transition-colors sm:text-base ${scrolled ? "text-navy" : "text-white"}`}>BRAHMANDNAYAK</span>
            <span className="font-display text-[0.55rem] font-semibold uppercase tracking-[0.3em] text-sky sm:text-[0.62rem] sm:tracking-[0.34em]">World Trade</span>
          </span>
        </Link>

        <div className="hidden items-center gap-9 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative font-medium text-sm tracking-wide transition-colors ${
                  scrolled
                    ? isActive ? "text-navy" : "text-navy/60 hover:text-navy"
                    : isActive ? "text-white" : "text-white/70 hover:text-white"
                } after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:bg-sky after:transition-all after:duration-300 ${
                  isActive ? "after:w-full" : "after:w-0 hover:after:w-full"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta"
            className={`group inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-300 ${
              scrolled ? "bg-navy text-white hover:bg-sky" : "bg-white text-navy hover:bg-sky hover:text-white"
            }`}
          >
            Send Requirement
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`flex h-11 w-11 items-center justify-center rounded-full border lg:hidden ${
            scrolled || open ? "border-black/10 bg-white/70 text-navy" : "border-white/30 bg-white/10 text-white"
          }`}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-black/5 bg-white/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col px-6 py-6">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `border-b border-black/5 py-4 font-display text-2xl font-bold tracking-tight ${
                      isActive ? "text-sky" : "text-navy"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white"
              >
                Send Your Requirement <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
